<script lang="ts">
  import type { FPost } from 'garden-common';
  import DefaultTag from '$lib/components/common/tagging/tags/Default.svelte';
  import MediaPreview from '../../common/media/MediaPreview.svelte';
  import PostStatsRow from '../../common/PostStatsRow.svelte';
  import Fullscreen from '$icons/FullscreenIcon.svelte';
  import PostButtonsRow from './PostButtonsRow.svelte';
  import SaveToGallery from '../../Modals/gallery/SaveToGallery.svelte';
  import CreateGallery from '../../Modals/gallery/CreateGallery.svelte';
  import AddToGallery from '../../Modals/gallery/AddToGallery.svelte';
  import { me } from '$stores/user.store';
  import DeletePostModal from '../../CreatePost/DeletePostModal.svelte';
  import DonateModal from '../../CreatePost/DonateModal.svelte';
  import UserWrapper from '../../common/user/userWrapper.svelte';
  import dayjs from 'dayjs';
  import ThreeDotMenu from '../../posts/ThreeDotMenu.svelte';
  import { PostsCore } from '$src/utils/postsCore.util';
  import type { WithCreatorData } from 'garden-common';
  import { goto } from '$app/navigation';
  import { openPostDetailsModal, postinDetailsModal, selectedPostDetailsTab } from '$src/stores';
  import PostCatsRow from '$lib/components/common/PostCatsRow.svelte';

  export let post: Partial<FPost<string>> & Partial<WithCreatorData>;

  let core = new PostsCore($me, post);

  async function setPost() {
    post = core.getPost();
  }

  let threeDotIgnoredTargets = [];
</script>

{#if core.openModal === 'save-to-gallery'}
  <SaveToGallery
    postId={post?._id}
    on:ModalClose={() => (core.openModal = null)}
    on:Saved={() => {
      core.SavePostToGallery();
      setPost();
    }}
    on:OpenCreateGallery={() => {
      core.OpenCreateGallery();
      setPost();
    }}
  />
{:else if core.openModal === 'create-gallery'}
  <CreateGallery
    on:GalleryCreated={() => {
      core.openModal = core.CloseCreateGallery();
    }}
    on:ModalClose={() => {
      core.openModal = core.CloseCreateGallery();
    }}
  />
{:else if core.openModal === 'add-to-gallery'}
  <AddToGallery
    on:ModalClose={() => {
      core.openModal = core.Close();
    }}
    on:AddedToGalleries={() => {
      core.openModal = core.Close();
    }}
  />
{:else if core.openModal === 'delete-post'}
  <DeletePostModal
    on:DeletePost={async () => {
      core.openModal = core.Close();
      await core.DeletePost();
    }}
    {post}
    on:ModalClose={() => {
      core.openModal = core.Close();
    }}
  />
{:else if core.openModal === 'donation-post'}
  <DonateModal
    on:Donate={e => {
      core.Donation(e);
      setPost();
    }}
    {post}
    on:ModalClose={() => {
      core.openModal = core.Close();
    }}
  />
{/if}

<div class="relative">
  <div class="shadow-island bg-white lg:rounded-2xl blur-none transition-none py-4 w-full">
    <!-- Post header -->
    <div>
      <div class="flex gap-x-4 px-4 w-full">
        <div class="flex flex-grow flex-row items-end justify-start">
          <div class=" flex flex-row">
            <UserWrapper user={post.creatorData} padding={false} />
          </div>
          <div
            class="flex justify-end items-end gap-x-2 text-blueish-grey-600 font-switzer text-sm"
          >
            <div>&bull;</div>
            <div>{dayjs(post.createdAt).fromNow(false)}</div>
          </div>
        </div>

        <!-- Three dot button -->
        <ThreeDotMenu
          {post}
          {threeDotIgnoredTargets}
          on:CopyLink={() => {
            core.onCopyLink();
          }}
          on:DeletePost={async () => {
            core.openModal = core.Close();
            await core.DeletePost();
          }}
          on:ViewProfile={() => {
            core.openModal = core.Close();
            core.onViewProfile();
          }}
          on:EditPost={() => {
            goto(`/post/edit/${post._id}`);
          }}
          on:save={() => {
            core.onSave();
            setPost();
          }}
        />
      </div>

      <div class="px-4 pt-4">
        <PostCatsRow {post} />
      </div>
    </div>

    <!-- Post title and description -->
    <div class="text-slate-900 pt-[26px] px-4">
      <div class="text-xl font-bold text-blueish-grey-900 break-words">{post.title}</div>
      <div class="mt-2 font-switzer break-words">{post.description}</div>
    </div>

    <!-- Post content -->
    <button class="mt-4 h-80 w-full">
      <MediaPreview
        on:clickedMedia={() => {
          core.onPreviewPost();
        }}
        {post}
      />
    </button>

    <!-- Post stats icons -->
    <div class="mt-4 px-4 flex">
      <div class="flex-grow">
        <PostStatsRow
          {post}
          on:donate={() => {
            postinDetailsModal.set(post);
            selectedPostDetailsTab.set('seeds');
            openPostDetailsModal.set(true);
          }}
          on:appreciate={() => {
            postinDetailsModal.set(post);
            selectedPostDetailsTab.set('likes');
            openPostDetailsModal.set(true);
          }}
          on:comment={() => {
            postinDetailsModal.set(post);
            openPostDetailsModal.set(true);
          }}
          on:save={() => {
            postinDetailsModal.set(post);
            selectedPostDetailsTab.set('saves');
            openPostDetailsModal.set(true);
          }}
        />
      </div>

      <button
        on:click={() => {
          core.onPreviewPost();
        }}
      >
        <Fullscreen class="stroke-garden-grey-600" />
      </button>
    </div>

    <!-- Separator -->
    <div class="border-neutral-800 border-b-[1px] border-opacity-5 mt-4 mx-4 mb-4" />

    <!-- Post buttons row -->
    <div class="px-8">
      <PostButtonsRow
        {post}
        on:donate={() => {
          core.onDonate();
          setPost();
        }}
        on:appreciate={() => {
          core.onAppreciate();
          setPost();
        }}
        on:comment={async () => {
          core.onComment();
          setPost();
        }}
        on:save={() => {
          core.onSave();
          setPost();
        }}
      />
    </div>
  </div>
</div>
